import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useFleetSidebar } from "@/features/brewcontrol/use-fleet-sidebar";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { trpc } from "@/hooks/trpc";
import { RequireCapability } from "@/components/require-capability";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/brewcontrol/enrol")({
  component: () => (
    <RequireCapability cap="brewcontrol-nodes:manage">
      <EnrolPage />
    </RequireCapability>
  ),
});

const enrolSchema = z.object({
  displayName: z.string().min(1, "Name is required"),
});

type EnrolForm = z.infer<typeof enrolSchema>;

/**
 * Enrolment mints a one-shot token, not a node. The machine becomes a node when
 * it dials in and redeems the token for a long-lived credential — which is why
 * this form asks for a name and nothing else. There is no host, no port and no
 * heartbeat interval to configure, because BrewBase never reaches out.
 */
function EnrolPage() {
  useFleetSidebar();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  // The token is a one-time secret that exists only in this mutation's
  // response — it is never a server-readable field, so it cannot be read from
  // the query cache and genuinely has to live in local state.
  const [issued, setIssued] = useState<{ token: string; name: string } | null>(
    null,
  );

  const form = useForm<EnrolForm>({
    resolver: zodResolver(enrolSchema),
    defaultValues: { displayName: "" },
  });

  const enrol = trpc.brewcontrol.enrolNode.useMutation({
    onSuccess: (result) => {
      setIssued({
        token: result.enrolmentToken,
        name: result.node.display_name,
      });
      form.reset();
      void utils.brewcontrol.listNodes.invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-3xl px-6 py-6 space-y-6">
        <div>
          <h1 className="text-sm font-medium">Enrol a node</h1>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Reserves a node and issues a single-use enrolment token. Ansible
            places the token on the machine; the node exchanges it for a
            long-lived credential the first time it dials in.
          </p>
        </div>

        <form
          className="space-y-4"
          onSubmit={form.handleSubmit((values) => enrol.mutate(values))}
        >
          <div className="space-y-2">
            <Label htmlFor="displayName">Display name</Label>
            <Input
              id="displayName"
              placeholder="Fermenter A"
              {...form.register("displayName")}
            />
            {form.formState.errors.displayName && (
              <p className="text-xs text-red-11">
                {form.formState.errors.displayName.message}
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={enrol.isPending}>
              {enrol.isPending ? "Issuing…" : "Issue enrolment token"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => void navigate({ to: "/brewcontrol" })}
            >
              Back to fleet
            </Button>
          </div>
        </form>

        {issued && (
          <div className="border border-dashed bg-muted/40 p-4 space-y-2">
            <div className="text-sm font-medium">
              Enrolment token for {issued.name}
            </div>
            <p className="text-xs text-muted-foreground">
              Shown once. Place it at
              <span className="font-mono"> /etc/brewcontrol/enrolment-token </span>
              on the node, mode 0600. It expires in 72 hours and is consumed on
              first use.
            </p>
            <Textarea
              readOnly
              className="font-mono text-xs"
              rows={2}
              value={issued.token}
            />
            <p className="text-xs text-muted-foreground">
              The node will appear in the fleet awaiting approval once it dials
              in. It receives no configuration until you approve it.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
